import React from 'react';

/**
 * Reusable form input component - Stitch design system
 * Gray background, uppercase labels, icon support, matching CustomDropdown style
 *
 * @param {Object} props
 * @param {React.Component} props.icon - Left icon inside the input field
 * @param {React.Component} props.labelIcon - Icon displayed next to label
 * @param {string} props.label - Input label (displayed uppercase)
 * @param {string} props.name - Input name
 * @param {string} props.type - Input type (text, email, tel, url)
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler (name, value)
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.error - Validation error message
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {React.ReactNode} props.rightIcon - Icon/element on the right side
 */
const FormInput = ({
    icon: Icon,
    labelIcon: LabelIcon,
    label,
    name,
    type = 'text',
    value,
    onChange,
    placeholder,
    error,
    required = false,
    disabled = false,
    rightIcon: RightIcon
}) => {
    const inputId = name ? `input-${name}` : undefined;

    return (
        <div>
            {label && (
                <label htmlFor={inputId} className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    {LabelIcon && <LabelIcon className="w-3.5 h-3.5 text-slate-400" />}
                    {label} {required && '*'}
                </label>
            )}
            <div className="relative">
                {Icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Icon className="w-4 h-4" />
                    </div>
                )}
                <input
                    id={inputId}
                    type={type}
                    name={name}
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={(e) => onChange(name, e.target.value)}
                    className={`w-full ${Icon ? 'pl-9' : 'px-3'} ${RightIcon ? 'pr-9' : 'pr-3'} py-2 bg-slate-50 border rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cabaza-purple/50 focus:border-cabaza-purple transition-all ${
                        error ? 'border-cabaza-pink' : 'border-slate-200'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                {RightIcon && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {RightIcon}
                    </div>
                )}
            </div>
            {error && <p className="mt-1 text-xs text-cabaza-pink">{error}</p>}
        </div>
    );
};

export default FormInput;
